package com.arena_web.di

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.remember
import com.arena_web.data.api.*
import com.arena_web.data.dto.*
import com.arena_web.data.http.HttpClientFactory
import com.arena_web.data.repository.*
import com.arena_web.data.websocket.UserWebSocketClient
import com.arena_web.data.websocket.UserWsEvent
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import com.arena_web.domain.repositories.*
import com.arena_web.domain.repositories.ActivityRepository
import com.arena_web.domain.usecases.auth.DeleteAccountUseCase
import com.arena_web.domain.usecases.auth.LoginUseCase
import com.arena_web.domain.usecases.auth.LogoutUseCase
import com.arena_web.domain.usecases.auth.OAuthLoginUseCase
import com.arena_web.domain.usecases.auth.RefreshTokenUseCase
import com.arena_web.domain.usecases.auth.RegisterUseCase
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import io.ktor.client.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow


class AppContainer {
    val userWsClient = UserWebSocketClient()
    val defaultHttpClient: HttpClient by lazy { HttpClientFactory.createDefault() }

    // Long-living scope for the user WS event collector below — outlives
    // any individual page composition so the header badges keep ticking
    // even when no AppHeader is currently mounted.
    private val containerScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private var headerSignalsJob: Job? = null

    // ── Header signals ──
    private val _highlightBusinessSwitch = MutableStateFlow(false)
    val highlightBusinessSwitch: StateFlow<Boolean> = _highlightBusinessSwitch.asStateFlow()

    fun requestBusinessSwitchHighlight() { _highlightBusinessSwitch.value = true }
    fun clearBusinessSwitchHighlight() { _highlightBusinessSwitch.value = false }

    // ── Business clubs cache (survives page navigation) ──
    private val _clubs = MutableStateFlow<List<ClubDto>>(emptyList())
    val clubs: StateFlow<List<ClubDto>> = _clubs.asStateFlow()

    private val _selectedClubId = MutableStateFlow<String?>(Storage.get(StorageKeys.SELECTED_CLUB_ID))
    val selectedClubId: StateFlow<String?> = _selectedClubId.asStateFlow()

    private val _clubsLoaded = MutableStateFlow(false)
    val clubsLoaded: StateFlow<Boolean> = _clubsLoaded.asStateFlow()

    // ── Notifications cache (survives page navigation) ──
    private val _unreadNotificationCount = MutableStateFlow(0)
    val unreadNotificationCount: StateFlow<Int> = _unreadNotificationCount.asStateFlow()

    private val _notifications = MutableStateFlow<List<NotificationDto>>(emptyList())
    val notifications: StateFlow<List<NotificationDto>> = _notifications.asStateFlow()

    private val _hasMoreNotifications = MutableStateFlow(true)
    val hasMoreNotifications: StateFlow<Boolean> = _hasMoreNotifications.asStateFlow()

    private var notificationCountLoaded = false
    private var notificationsListLoaded = false

    /** Cheap unread-count fetch — used on page load for the badge. */
    suspend fun ensureNotificationsCountLoaded() {
        if (notificationCountLoaded) return
        val repo = notificationRepository ?: return
        repo.getNotifications(mapOf("page" to "0", "size" to "0")).onSuccess { page ->
            _unreadNotificationCount.value = page.unreadCount.toInt()
            notificationCountLoaded = true
        }
    }

    /** Full first-page fetch — only when the notification dropdown is opened. */
    suspend fun ensureNotificationsListLoaded() {
        if (notificationsListLoaded) return
        refreshNotifications()
    }

    suspend fun refreshNotifications() {
        val repo = notificationRepository ?: return
        repo.getNotifications(mapOf("page" to "0", "size" to "5")).onSuccess { page ->
            _unreadNotificationCount.value = page.unreadCount.toInt()
            _notifications.value = page.notifications
            _hasMoreNotifications.value = page.notifications.size >= 5
            notificationCountLoaded = true
            notificationsListLoaded = true
        }
    }

    fun updateNotificationCount(count: Int) {
        _unreadNotificationCount.value = count
    }

    fun updateNotifications(list: List<NotificationDto>, unread: Int, hasMore: Boolean) {
        _notifications.value = list
        _unreadNotificationCount.value = unread
        _hasMoreNotifications.value = hasMore
    }

    // ── Sports cache ──
    // Stale-while-revalidate: hydrate from localStorage on first access
    // (so the dropdown opens instantly across page reloads) and refresh
    // from the backend in the same call — if the server payload differs,
    // the StateFlow swaps and the dropdown updates seamlessly.
    private val _sportsList = MutableStateFlow<List<SportDto>>(emptyList())
    val sportsList: StateFlow<List<SportDto>> = _sportsList.asStateFlow()
    private var sportsRefreshed = false

    suspend fun ensureSportsLoaded() {
        // Step 1: warm the StateFlow from the local cache so callers see
        // a populated list immediately. Skipped if we've already hydrated
        // this session or if the cache is empty/corrupt.
        if (_sportsList.value.isEmpty()) {
            readCachedSports()?.let { _sportsList.value = it }
        }

        // Step 2: refresh from the backend at most once per session (the
        // sports catalog changes rarely — no need to refetch on every
        // dropdown open within the same tab).
        if (sportsRefreshed) return
        sportRepository.getAllSports().onSuccess { sports ->
            sportsRefreshed = true
            if (sports != _sportsList.value) {
                _sportsList.value = sports
                writeCachedSports(sports)
            }
            if (Storage.get(StorageKeys.SELECTED_SPORT_ID) == null) {
                // First-time visitors land on the cross-sport "All" view —
                // they haven't expressed a preference yet, so showing every
                // sport (incl. Other) is more useful than guessing Padel.
                // Stored explicitly so downstream code can read storage as
                // a single source of truth (no special-case for "null").
                Storage.save(StorageKeys.SELECTED_SPORT_ID, "")
                Storage.save(StorageKeys.SELECTED_SPORT_NAME, "All")
            }
        }
    }

    private fun readCachedSports(): List<SportDto>? {
        val raw = Storage.get(StorageKeys.SPORTS_LIST_CACHE) ?: return null
        return try {
            kotlinx.serialization.json.Json.decodeFromString<List<SportDto>>(raw)
        } catch (_: Throwable) {
            // Schema drift or corrupt payload — drop it; the live fetch
            // below will rewrite the cache with a valid version.
            Storage.remove(StorageKeys.SPORTS_LIST_CACHE)
            null
        }
    }

    private fun writeCachedSports(sports: List<SportDto>) {
        try {
            Storage.save(
                StorageKeys.SPORTS_LIST_CACHE,
                kotlinx.serialization.json.Json.encodeToString(sports),
            )
        } catch (_: Throwable) { /* best-effort cache */ }
    }

    // ── Activity cache ──
    private val _activityData = MutableStateFlow<ActivityDto?>(null)
    val activityData: StateFlow<ActivityDto?> = _activityData.asStateFlow()

    private val _activityCount = MutableStateFlow(0)
    val activityCount: StateFlow<Int> = _activityCount.asStateFlow()

    private var activityCountLoaded = false
    private var activityLoaded = false

    /** Cheap count fetch — used on page load for the badge. */
    suspend fun ensureActivityCountLoaded() {
        if (activityCountLoaded) return
        activityRepository?.getActivityCount()?.onSuccess { count ->
            _activityCount.value = count.total.toInt()
            activityCountLoaded = true
        }
    }

    /** Full activity (events + bookings) — only when the dropdown opens. */
    suspend fun ensureActivityLoaded() {
        if (activityLoaded) return
        activityRepository?.getActivity()?.onSuccess { activity ->
            _activityData.value = activity
            _activityCount.value = activity.totalCount
            activityLoaded = true
            activityCountLoaded = true
        }
    }

    /**
     * Mark the cached activity list stale so the next dropdown-open re-fetches
     * the full payload, and refresh the badge count immediately via the cheap
     * `/activity/count` endpoint so the header reflects any backend-side
     * changes (e.g. an event finishing transitions out of the "active" list).
     *
     * Used by background screens (event detail, etc.) that don't render the
     * dropdown but observe state changes which would otherwise leave the
     * cached list stale.
     */
    suspend fun refreshActivity() {
        activityLoaded = false
        activityCountLoaded = false
        // Refresh the cheap count so the badge stays accurate without
        // paying the cost of the full `/activity` list — the list itself
        // re-fetches lazily when the dropdown is next opened.
        activityRepository?.getActivityCount()?.onSuccess { count ->
            _activityCount.value = count.total.toInt()
            activityCountLoaded = true
        }
    }

    // ── Pricing cache ──
    // Public endpoint, fetched once per session. Loaded eagerly so the
    // AvatarGenerationModal can render the live price without waiting.
    private val _pricing = MutableStateFlow<PricingDto?>(null)
    val pricing: StateFlow<PricingDto?> = _pricing.asStateFlow()
    private var pricingLoaded = false

    suspend fun ensurePricingLoaded() {
        if (pricingLoaded) return
        pricingRepository.getPricing().onSuccess { p ->
            _pricing.value = p
            pricingLoaded = true
        }
    }

    // ── Wallet cache ──
    private val _walletBalance = MutableStateFlow<String?>(Storage.get(StorageKeys.WALLET_BALANCE))
    val walletBalance: StateFlow<String?> = _walletBalance.asStateFlow()

    private val _walletCurrency = MutableStateFlow(Storage.get(StorageKeys.WALLET_CURRENCY) ?: "USD")
    val walletCurrency: StateFlow<String> = _walletCurrency.asStateFlow()

    private val _walletTransactions = MutableStateFlow<List<WalletTransactionDto>>(emptyList())
    val walletTransactions: StateFlow<List<WalletTransactionDto>> = _walletTransactions.asStateFlow()

    private var walletLoaded = false

    suspend fun ensureWalletLoaded() {
        if (walletLoaded) return
        refreshWallet()
    }

    suspend fun refreshWallet() {
        walletRepository?.getWallet()?.onSuccess { wallet ->
            _walletBalance.value = wallet.balance
            _walletCurrency.value = wallet.currency
            Storage.save(StorageKeys.WALLET_BALANCE, wallet.balance)
            Storage.save(StorageKeys.WALLET_CURRENCY, wallet.currency)
            walletLoaded = true
        }
        // Transactions are not needed for the header — fetch them on-demand
        // (e.g. when a wallet-detail screen opens) via `refreshWalletTransactions()`.
    }

    suspend fun refreshWalletTransactions() {
        walletRepository?.getTransactions(limit = 50, offset = 0)
            ?.onSuccess { txns -> _walletTransactions.value = txns }
    }

    fun updateWalletBalance(balance: String) {
        _walletBalance.value = balance
        Storage.save(StorageKeys.WALLET_BALANCE, balance)
    }

    // ── People interactions count (header heart badge) ──
    private val _interactionsNewLikesCount = MutableStateFlow(0)
    val interactionsNewLikesCount: StateFlow<Int> = _interactionsNewLikesCount.asStateFlow()
    private var interactionsCountLoaded = false

    suspend fun ensureInteractionsCountLoaded() {
        if (interactionsCountLoaded) return
        peopleRepository?.getInteractionsCount()?.onSuccess { c ->
            _interactionsNewLikesCount.value = c.newLikes
            interactionsCountLoaded = true
        }
    }

    /** Re-fetches the heart-badge count from the cheap count endpoint. Used
     *  by the WS `INTERACTIONS_UPDATE` handler so the badge moves in real
     *  time when someone likes the caller / a match is formed. */
    suspend fun refreshInteractionsCount() {
        peopleRepository?.getInteractionsCount()?.onSuccess { c ->
            _interactionsNewLikesCount.value = c.newLikes
            interactionsCountLoaded = true
        }
    }

    fun updateInteractionsNewLikesCount(count: Int) {
        _interactionsNewLikesCount.value = count
    }


    suspend fun ensureClubsLoaded() {
        if (_clubsLoaded.value) return
        val repo = clubAdminRepository ?: return
        repo.getMyClubs().onSuccess { loaded ->
            _clubs.value = loaded
            _clubsLoaded.value = true
            val savedId = Storage.get(StorageKeys.SELECTED_CLUB_ID)
            val match = loaded.find { it.id == savedId } ?: loaded.firstOrNull()
            if (match != null) {
                selectClub(match.id)
            }
        }
    }

    fun selectClub(clubId: String?) {
        _selectedClubId.value = clubId
        if (clubId != null) {
            Storage.save(StorageKeys.SELECTED_CLUB_ID, clubId)
        } else {
            Storage.remove(StorageKeys.SELECTED_CLUB_ID)
        }
    }

    fun invalidateClubs() {
        _clubsLoaded.value = false
    }

    private var authenticatedClient: HttpClient? = null

    // ── Public APIs (no auth required) ──
    private val authApi: AuthApi by lazy { AuthApi(defaultHttpClient) }
    private val sportApi: SportApi by lazy { SportApi(defaultHttpClient) }
    private val clubPublicApi: ClubPublicApi by lazy { ClubPublicApi(defaultHttpClient) }
    private val publicEventApi: EventApi by lazy { EventApi(defaultHttpClient) }
    private val publicUserApi: UserApi by lazy { UserApi(defaultHttpClient) }
    private val pricingApi: PricingApi by lazy { PricingApi(defaultHttpClient) }

    private val publicFeedApi: FeedApi by lazy { FeedApi(defaultHttpClient) }
    private val publicPeopleApi: PeopleApi by lazy { PeopleApi(defaultHttpClient) }

    // ── Authenticated APIs ──
    private var userApi: UserApi? = null
    private var followApi: FollowApi? = null
    private var eventApi: EventApi? = null
    private var teamApi: TeamApi? = null
    private var chatApi: ChatApi? = null
    private var bookingApi: BookingApi? = null
    private var clubAdminApi: ClubAdminApi? = null
    private var mediaApi: MediaApi? = null
    private var notificationApi: NotificationApi? = null
    private var cardApi: CardApi? = null
    private var activityApi: ActivityApi? = null
    private var walletApi: WalletApi? = null
    private var feedApi: FeedApi? = null
    private var userPostApi: UserPostApi? = null
    private var userStoryApi: UserStoryApi? = null
    private var peopleApi: PeopleApi? = null

    /** Google Places proxy — public: the address autocomplete helper in
     *  utils/AddressAutocomplete.kt takes it straight from the container. */
    var placesApi: PlacesApi? = null
        private set

    // ── Public Repositories ──
    val authRepository: AuthRepository by lazy { AuthRepositoryImpl(authApi) }
    val sportRepository: SportRepository by lazy { SportRepositoryImpl(sportApi) }
    val clubPublicRepository: ClubPublicRepository by lazy { ClubPublicRepositoryImpl(clubPublicApi) }
    val publicEventRepository: EventRepository by lazy { EventRepositoryImpl(publicEventApi) }
    val publicUserRepository: UserRepository by lazy { UserRepositoryImpl(publicUserApi) }
    private val publicTeamApi: TeamApi by lazy { TeamApi(defaultHttpClient) }
    val publicTeamRepository: TeamRepository by lazy { TeamRepositoryImpl(publicTeamApi) }
    val publicFeedRepository: FeedRepository by lazy { FeedRepositoryImpl(publicFeedApi) }
    val publicPeopleRepository: PeopleRepository by lazy { PeopleRepositoryImpl(publicPeopleApi) }
    val pricingRepository: PricingRepository by lazy { PricingRepositoryImpl(pricingApi) }

    // ── Authenticated Repositories ──
    var userRepository: UserRepository? = null; private set
    var followRepository: FollowRepository? = null; private set
    var eventRepository: EventRepository? = null; private set
    var teamRepository: TeamRepository? = null; private set
    var chatRepository: ChatRepository? = null; private set
    var bookingRepository: BookingRepository? = null; private set
    var clubAdminRepository: ClubAdminRepository? = null; private set
    var notificationRepository: NotificationRepository? = null; private set
    var cardRepository: CardRepository? = null; private set
    var activityRepository: ActivityRepository? = null; private set
    var walletRepository: WalletRepository? = null; private set
    var feedRepository: FeedRepository? = null; private set
    var userPostRepository: UserPostRepository? = null; private set
    var userStoryRepository: UserStoryRepository? = null; private set
    var peopleRepository: PeopleRepository? = null; private set

    // Person handed off from the header Interactions popup → People page. Set
    // when the user taps a "New likes" card; the People page consumes it on
    // mount, prepends the person to the swipe queue, and clears the slot.
    private val _pendingFocusPerson = MutableStateFlow<PersonDto?>(null)
    val pendingFocusPerson: StateFlow<PersonDto?> = _pendingFocusPerson.asStateFlow()
    fun setPendingFocusPerson(person: PersonDto?) {
        _pendingFocusPerson.value = person
    }

    // ── Use cases ──
    private val oauthLoginUseCase: OAuthLoginUseCase by lazy { OAuthLoginUseCase(authRepository) }
    private val loginUseCase: LoginUseCase by lazy { LoginUseCase(authRepository) }
    private val registerUseCase: RegisterUseCase by lazy { RegisterUseCase(authRepository) }
    private val logoutUseCase: LogoutUseCase by lazy { LogoutUseCase(authRepository) }
    private val refreshTokenUseCase: RefreshTokenUseCase by lazy { RefreshTokenUseCase(authRepository) }

    fun getOAuthLoginUseCase(): OAuthLoginUseCase = oauthLoginUseCase
    fun getLoginUseCase(): LoginUseCase = loginUseCase
    fun getRegisterUseCase(): RegisterUseCase = registerUseCase
    fun getLogoutUseCase(): LogoutUseCase = logoutUseCase
    fun getRefreshTokenUseCase(): RefreshTokenUseCase = refreshTokenUseCase
    fun getDeleteAccountUseCase(): DeleteAccountUseCase {
        val repo = userRepository
            ?: throw IllegalStateException("Authenticated UserRepository not available")
        return DeleteAccountUseCase(repo)
    }

    fun createAuthenticatedClients(token: String) {
        authenticatedClient?.close()
        val client = HttpClientFactory.createAuthenticated(token)
        authenticatedClient = client

        // APIs
        val userApiInstance = UserApi(client)
        val followApiInstance = FollowApi(client)
        val eventApiInstance = EventApi(client)
        val teamApiInstance = TeamApi(client)
        val chatApiInstance = ChatApi(client)
        val bookingApiInstance = BookingApi(client)
        val clubAdminApiInstance = ClubAdminApi(client)
        val notificationApiInstance = NotificationApi(client)

        userApi = userApiInstance
        followApi = followApiInstance
        eventApi = eventApiInstance
        teamApi = teamApiInstance
        chatApi = chatApiInstance
        bookingApi = bookingApiInstance
        clubAdminApi = clubAdminApiInstance
        mediaApi = MediaApi(client)
        notificationApi = notificationApiInstance
        val cardApiInstance = CardApi(client)
        cardApi = cardApiInstance
        val activityApiInstance = ActivityApi(client)
        activityApi = activityApiInstance
        val walletApiInstance = WalletApi(client)
        walletApi = walletApiInstance
        val feedApiInstance = FeedApi(client)
        feedApi = feedApiInstance
        val userPostApiInstance = UserPostApi(client)
        userPostApi = userPostApiInstance
        val userStoryApiInstance = UserStoryApi(client)
        userStoryApi = userStoryApiInstance
        val peopleApiInstance = PeopleApi(client)
        peopleApi = peopleApiInstance
        placesApi = PlacesApi(client)

        // Repositories
        userRepository = UserRepositoryImpl(userApiInstance)
        followRepository = FollowRepositoryImpl(followApiInstance)
        eventRepository = EventRepositoryImpl(eventApiInstance)
        teamRepository = TeamRepositoryImpl(teamApiInstance)
        chatRepository = ChatRepositoryImpl(chatApiInstance)
        bookingRepository = BookingRepositoryImpl(bookingApiInstance)
        clubAdminRepository = ClubAdminRepositoryImpl(clubAdminApiInstance)
        notificationRepository = NotificationRepositoryImpl(notificationApiInstance)
        cardRepository = CardRepositoryImpl(cardApiInstance)
        activityRepository = ActivityRepositoryImpl(activityApiInstance)
        walletRepository = WalletRepositoryImpl(walletApiInstance)
        feedRepository = FeedRepositoryImpl(feedApiInstance)
        userPostRepository = UserPostRepositoryImpl(userPostApiInstance)
        userStoryRepository = UserStoryRepositoryImpl(userStoryApiInstance)
        peopleRepository = PeopleRepositoryImpl(peopleApiInstance)

        userWsClient.connect()
        startHeaderSignalCollectors()
    }

    /**
     * One coroutine that owns the header badge counters end-to-end:
     *
     *  1. Fires the three cheap count fetches once (`/notifications?size=0`,
     *     `/activity/count`, `/people/interactions/count`) so the badges are
     *     accurate immediately after login.
     *  2. Subscribes to the global user WS and refreshes the relevant counter
     *     whenever a delta arrives (`NewNotification`, `ActivityUpdate`,
     *     `InteractionsUpdate`).
     *  3. Re-fires the count fetches on every `Connected` event so a fresh
     *     WS handshake after an offline window resyncs anything we may have
     *     missed while disconnected.
     *
     * Because this lives in the container (not in `AppHeader`), screen
     * navigation no longer re-triggers any of these REST calls.
     */
    private fun startHeaderSignalCollectors() {
        headerSignalsJob?.cancel()
        headerSignalsJob = containerScope.launch {
            // Initial population — `ensureXCountLoaded` are cached, so this
            // block is a no-op on subsequent re-entries (e.g. WS reconnect
            // calls `startHeaderSignalCollectors` again).
            ensureNotificationsCountLoaded()
            ensureActivityCountLoaded()
            ensureInteractionsCountLoaded()

            userWsClient.events.collect { event ->
                when (event) {
                    is UserWsEvent.NewNotification -> refreshNotifications()
                    is UserWsEvent.ActivityUpdate -> refreshActivity()
                    is UserWsEvent.InteractionsUpdate -> refreshInteractionsCount()
                    is UserWsEvent.Connected -> {
                        // Re-sync after reconnect — invalidate so the cache
                        // guards don't short-circuit and re-fetch fresh values.
                        notificationCountLoaded = false
                        activityCountLoaded = false
                        interactionsCountLoaded = false
                        ensureNotificationsCountLoaded()
                        ensureActivityCountLoaded()
                        ensureInteractionsCountLoaded()
                    }
                    else -> Unit
                }
            }
        }
    }

    fun getAuthenticatedClient(): HttpClient {
        return authenticatedClient
            ?: throw IllegalStateException("Authenticated client not initialized. Call createAuthenticatedClients(token) first.")
    }

    fun clearAuthenticatedClients() {
        headerSignalsJob?.cancel()
        headerSignalsJob = null
        userWsClient.disconnect()
        authenticatedClient?.close()
        authenticatedClient = null

        userApi = null
        followApi = null
        eventApi = null
        teamApi = null
        chatApi = null
        bookingApi = null
        clubAdminApi = null
        cardApi = null
        mediaApi = null
        notificationApi = null

        cardRepository = null
        userRepository = null
        followRepository = null
        eventRepository = null
        teamRepository = null
        chatRepository = null
        bookingRepository = null
        clubAdminRepository = null
        notificationRepository = null
        activityApi = null
        activityRepository = null
        walletApi = null
        walletRepository = null
        feedApi = null
        feedRepository = null
        userPostApi = null
        userPostRepository = null
        userStoryApi = null
        userStoryRepository = null
        peopleApi = null
        peopleRepository = null
        placesApi = null

        // Reset caches
        _unreadNotificationCount.value = 0
        _notifications.value = emptyList()
        _hasMoreNotifications.value = true
        notificationCountLoaded = false
        notificationsListLoaded = false
        _clubs.value = emptyList()
        _selectedClubId.value = null
        _clubsLoaded.value = false
        _activityData.value = null
        _activityCount.value = 0
        activityLoaded = false
        activityCountLoaded = false
        _walletBalance.value = null
        _walletCurrency.value = "USD"
        _walletTransactions.value = emptyList()
        walletLoaded = false
        _interactionsNewLikesCount.value = 0
        interactionsCountLoaded = false
    }

    fun dispose() {
        defaultHttpClient.close()
        clearAuthenticatedClients()
    }
}

val LocalAppContainer = compositionLocalOf<AppContainer> {
    error("AppContainer not provided")
}

@Composable
fun ProvideAppContainer(
    container: AppContainer = remember { AppContainer() },
    content: @Composable () -> Unit
) {
    CompositionLocalProvider(LocalAppContainer provides container) {
        content()
    }
}
